'use client';

import { FileText, Download, ExternalLink } from 'lucide-react';
import { getPublicMediaUrl } from '@/core/utils/media';
import { useTranslation } from '@/core/components/providers/TranslationProvider';

interface Attachment {
  id: number;
  title: string;
  file_type?: string;
  file_path?: string;
  variant_id?: number | null;
}

interface ProductAttachmentsBlockProps {
  attachments: Attachment[];
  selectedVariantId?: number | null;
}

export default function ProductAttachmentsBlock({ attachments, selectedVariantId }: ProductAttachmentsBlockProps) {
  const { t } = useTranslation();
  if (!attachments || attachments.length === 0) return null;

  // Filter attachments for active variant or global product attachments
  const filteredAttachments = attachments.filter(
    (att) => !att.variant_id || (selectedVariantId && att.variant_id === selectedVariantId)
  );

  if (filteredAttachments.length === 0) return null;

  return (
    <div className="mt-8 border border-slate-200 rounded-2xl p-6 bg-slate-50/50 shadow-sm">
      <div className="flex items-center gap-2.5 mb-4">
        <FileText className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-bold text-slate-900">{t('product.downloads_title', 'Pliki do pobrania')}</h3>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {filteredAttachments.map((att) => {
          const url = att.file_path ? getPublicMediaUrl(att.file_path) : '#';
          return (
            <a
              key={att.id}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3.5 bg-white border border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="truncate">
                  <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-blue-600 transition-colors">
                    {att.title}
                  </p>
                  <span className="text-[11px] font-medium text-slate-400 uppercase">
                    {att.file_type || 'PDF'}
                  </span>
                </div>
              </div>
              <Download className="h-4 w-4 text-slate-400 group-hover:text-blue-600 transition-colors shrink-0 ml-2" />
            </a>
          );
        })}
      </div>
    </div>
  );
}
